import { apiFetch } from "../../../auth/apiClient";
import { paymentDocumentTypeLabel } from "../../../lib/paymentDocumentLabels";

async function readResponse(response, fallback) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.message || fallback);
    error.code = payload.error || payload.code;
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}
const uncertainUploadMessage = "PATH could not confirm whether the document was saved. Check the case documents before uploading again. If it is missing, ask a system administrator to review the upload.";
const uncertainUploadError = () => Object.assign(new Error(uncertainUploadMessage), {
  code: "document_upload_outcome_uncertain",
  payload: { retrySafe: false, manualReviewRequired: true },
});
function uploadErrorMessage(payload, status) {
  if (payload.retrySafe === false || payload.manualReviewRequired === true) return uncertainUploadMessage;
  switch (payload.error || payload.code) {
    case "document_store_failed": return "PATH could not save this document to the case. Please try again.";
    case "object_store_upload_failed": return "PATH could not store the file. Please try again.";
    case "file_too_large": {
      const maxBytes = Number(payload.maxBytes);
      return Number.isFinite(maxBytes) && maxBytes > 0
        ? `The file is too large. Choose a file no larger than ${Math.ceil(maxBytes / (1024 * 1024))} MB.`
        : "The file is too large to upload. Choose a smaller file.";
    }
    case "unsupported_file_type": return "That file type is not supported. Choose a PDF, Word document, JPG, PNG, BMP or TIFF file.";
    case "ei_verification_forbidden": return "You do not have permission to upload EI verification documents.";
    case "file_required": return "Choose a file to upload.";
    case "invalid_document_type": return "This document type is unavailable. Choose another type or ask a system administrator to check the configuration.";
    case "application_required_for_document": return "This document needs a linked application. Check the intervention’s application before uploading.";
    case "applicant_user_required": return "PATH could not identify the applicant for this application. Ask an administrator to check the client record before uploading.";
    case "applicant_client_mismatch":
    case "applicant_user_not_found":
    case "applicant_identity_conflict": return "The applicant link for this application needs attention. Ask an administrator to check the client record before uploading.";
    case "document_scope_changed": return "The document’s case or application changed. Reload the client file before uploading.";
    default:
      if (status === 401) return "Your session has expired. Sign in again before uploading.";
      if (status === 403) return "You no longer have access to upload this document. Reload the client file.";
      return "The document could not be uploaded. Please try again or ask a system administrator for help.";
  }
}
async function readUploadResponse(response) {
  const decoded = await response.json().catch(() => ({}));
  const payload = decoded && typeof decoded === "object" ? decoded : {};
  if (!response.ok) {
    throw Object.assign(new Error(uploadErrorMessage(payload, response.status)), {
      code: payload.error || payload.code,
      status: response.status,
      payload,
    });
  }
  if (!payload.document?.id) throw uncertainUploadError();
  return payload;
}

const endpoint = caseId => `/api/cases/${encodeURIComponent(caseId)}/payment-email`;
export const paymentEmailApi = {
  async context(caseId, { interventionId, packetId, signal } = {}) {
    const query = new URLSearchParams();
    if (interventionId) query.set("interventionId", interventionId);
    if (packetId) query.set("packetId", packetId);
    return readResponse(await apiFetch(`${endpoint(caseId)}-context${query.toString() ? `?${query}` : ""}`, { signal }), "Unable to load payment email.");
  },
  async preview(caseId, draft, signal) {
    return readResponse(await apiFetch(`${endpoint(caseId)}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ draft }), signal }), "Unable to check this payment email.");
  },
  async save(caseId, draft, { action = "save", reviewToken, signal } = {}) {
    const result = await readResponse(await apiFetch(endpoint(caseId), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action, draft, ...(reviewToken ? { reviewToken } : {}) }), signal }), action === "send" ? "Unable to send this payment email." : "Unable to save this payment email.");
    // A lost or incomplete response cannot authorize a second mutation.
    if (!result.packetId || result.version == null || !result.draft) throw new Error("PATH returned an incomplete payment result.");
    return result;
  },
  async documentTypes({ signal } = {}) {
    const data = await readResponse(await apiFetch("/api/document-types", { signal }), "Unable to load document types.");
    if (!Array.isArray(data.items)) throw new Error("Unable to load document types.");
    const caseScopes = new Set(["client", "case", "application", "action_plan"]);
    return data.items.filter(type => type?.code && type.isActive !== false && caseScopes.has(type.scope || "application"))
      .map(type => ({ value: type.code, label: paymentDocumentTypeLabel(type.code, type.label), scope: type.scope || "application" }));
  },
  async upload(context, file, { documentType, label, requirement, intervention, signal } = {}) {
    const form = new FormData();
    form.append("file", file);
    form.append("label", label?.trim() || file.name);
    form.append("caseId", context.caseId);
    if (documentType) form.append("documentType", documentType);
    const interventionId = requirement?.interventionId || intervention?.id;
    const actionPlanId = requirement?.actionPlanId || intervention?.actionPlanId;
    const applicationId = requirement?.applicationId || intervention?.applicationId;
    if (interventionId) form.append("interventionId", interventionId);
    if (actionPlanId) form.append("actionPlanId", actionPlanId);
    if (applicationId) form.append("applicationId", applicationId);
    let response;
    try {
      response = await apiFetch(`/api/cases/${encodeURIComponent(context.caseId)}/documents/upload`, { method: "POST", body: form, signal });
    } catch (failure) {
      if (failure.name === "AbortError") throw failure;
      throw uncertainUploadError();
    }
    return readUploadResponse(response);
  },
  async documentUrl(documentId, packetId) {
    const query = packetId ? `?paymentPacketId=${encodeURIComponent(packetId)}` : "";
    const data = await readResponse(await apiFetch(`/api/documents/${encodeURIComponent(documentId)}/presign-download${query}`), "Unable to open this document.");
    if (!data.presigned?.url) throw new Error("Document preview is unavailable.");
    return data.presigned.url;
  },
};
